import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getNewsArticle, getNews, NEWSROOM_DISCLOSURE } from "@/lib/news";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { NEWSROOM_ID, breadcrumbs, ldScript, orgRef, personRef } from "@/lib/schema";
import { ReleaseTrackCluster } from "@/components/ReleaseTrackCluster";


export const Route = createFileRoute("/news/$slug")({
  loader: ({ params }) => {
    const article = getNewsArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "News · Not found" }, { name: "robots", content: "noindex" }] };
    }
    const a = loaderData.article;
    const url = `${SITE_URL}/news/${params.slug}`;
    const image = absoluteUrl(a.image.url);
    const modified = a.updatedAt ?? a.publishedAt;
    return {
      meta: [
        { title: `${a.title} | Buddha KA Newsroom` },
        { name: "description", content: a.dek },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.dek },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "article:published_time", content: a.publishedAt },
        { property: "article:modified_time", content: modified },
        { property: "article:author", content: "Buddha KA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: a.title },
        { name: "twitter:description", content: a.dek },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "@id": `${url}#article`,
          headline: a.title,
          description: a.dek,
          datePublished: a.publishedAt,
          dateModified: modified,
          image: [image],
          mainEntityOfPage: { "@type": "WebPage", "@id": url, url },
          url,
          inLanguage: "en-US",
          isPartOf: { "@id": NEWSROOM_ID },
          // Byline: "Buddha KA / KAfrequency Editorial" — both entities,
          // referenced by their sitewide @ids (no duplicate nodes).
          author: [personRef(), orgRef()],
          creator: personRef(),
          publisher: orgRef(),
          copyrightHolder: orgRef(),
          about: [personRef(), orgRef()],
          articleSection: a.category,
          keywords: a.tags.join(", "),
          // Internal destinations the editorial explicitly points at.
          ...(a.links.length
            ? {
                mentions: a.links.map((l) => ({
                  "@type": "CreativeWork",
                  name: l.label,
                  url: absoluteUrl(l.to),
                })),
                relatedLink: a.links.map((l) => absoluteUrl(l.to)),
              }
            : {}),
        }),
        ldScript(breadcrumbs([["News", "/news"], [a.title, `/news/${params.slug}`]])),
      ],

    };
  },
  component: NewsArticleView,
  notFoundComponent: NewsNotFound,
});

function NewsNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
      <h1 className="font-serif text-4xl">Story not found</h1>
      <p className="mt-4 text-muted-foreground">That newsroom URL doesn't match a published story.</p>
      <Link
        to="/news"
        className="mt-8 inline-flex rounded-full border border-gold px-6 py-3 text-sm text-gold hover:bg-gold hover:text-obsidian"
      >
        Back to the official newsroom
      </Link>
    </div>
  );
}


function NewsArticleView() {
  const { article } = Route.useLoaderData();
  const related = getNews().filter((n) => n.slug !== article.slug).slice(0, 3);
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const dateLabel = fmt(article.publishedAt);
  const modifiedLabel = article.updatedAt && article.updatedAt !== article.publishedAt ? fmt(article.updatedAt) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-gold">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/news" className="hover:text-gold">News</Link>
      </nav>

      <article>
        <header>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
            Official Newsroom · {article.category}
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl">{article.title}</h1>
          <p className="mt-4 text-lg text-ivory/85">{article.dek}</p>
          <p className="mt-4 text-xs text-muted-foreground">
            By {article.authorName} ·{" "}
            <time dateTime={article.publishedAt}>{dateLabel}</time>
            {modifiedLabel && (
              <>
                {" · Updated "}
                <time dateTime={article.updatedAt}>{modifiedLabel}</time>
              </>
            )}
          </p>
          <p className="mt-4 rounded-xl border hairline bg-obsidian/60 p-4 text-sm text-muted-foreground">
            {NEWSROOM_DISCLOSURE}
          </p>
        </header>

        <figure className="mt-8 overflow-hidden rounded-2xl border hairline bg-charcoal">
          <img
            src={article.image.url}
            alt={article.image.alt}
            className="aspect-[16/9] w-full object-cover"
            loading="eager"
            decoding="async"
          />
        </figure>

        <div className="prose prose-invert mt-10 max-w-none">
          {article.body.map((p: string, i: number) => (
            <p key={i} className="mt-5 text-lg leading-relaxed text-ivory/90">
              {p}
            </p>
          ))}
        </div>

        {article.links.length > 0 && (
          <section className="mt-10">
            <h2 className="font-serif text-2xl">Explore further</h2>
            <ul className="mt-4 flex flex-wrap gap-2 text-sm">
              {article.links.map((l: { to: string; label: string }) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="inline-flex rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {article.releaseSlug && <ReleaseTrackCluster releaseSlug={article.releaseSlug} />}
      </article>

      <aside className="mt-12 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground">
        <p>
          Reference this story: <span className="text-ivory">{article.authorName}</span> · Official Newsroom ·{" "}
          <time dateTime={article.publishedAt}>{dateLabel}</time>
        </p>
        <p className="mt-3">
          Media inquiries →{" "}
          <Link to="/contact" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Contact Buddha KA</Link>{" "}
          · Press kit →{" "}
          <Link to="/press" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA press kit</Link>{" "}
          · Biography →{" "}
          <Link to="/about" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">About Buddha KA</Link>
        </p>
      </aside>


      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif text-2xl">More from the newsroom</h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-3">
            {related.map((n) => (
              <li key={n.slug}>
                <Link
                  to="/news/$slug"
                  params={{ slug: n.slug }}
                  className="group block h-full rounded-xl border hairline bg-charcoal p-4 hover:border-gold"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{n.category}</p>
                  <h3 className="mt-2 font-serif text-lg text-ivory group-hover:text-gold-luminous">{n.title}</h3>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
